import type {
  SupplierOrganizationDto,
  SupplierOrganizationUpdatePayload,
} from '@/features/suppliers/lib/supplier-organization-types'

export type SupplierOrganizationFormState = {
  name: string
  localName: string
  businessRegistrationNumber: string
  street: string
  street2: string
  streetNumber: string
  country: string
  postCode: string
  poBox: string
  city: string
  subDivision: string
  localStreet: string
  localStreet2: string
  localStreetNumber: string
  localSubDivision: string
  localPostCode: string
  localPoBox: string
  localCity: string
  contactName: string
  contactEmail: string
  contactPhoneNumber: string
}

export function buildSupplierOrganizationFormState(
  supplier: SupplierOrganizationDto
): SupplierOrganizationFormState {
  return {
    name: supplier.name ?? '',
    localName: supplier.localName ?? '',
    businessRegistrationNumber: supplier.businessRegistrationNumber ?? '',
    street: supplier.street ?? '',
    street2: supplier.street2 ?? '',
    streetNumber: supplier.streetNumber ?? '',
    country: supplier.country ?? '',
    postCode: supplier.postCode ?? '',
    poBox: supplier.poBox ?? '',
    city: supplier.city ?? '',
    subDivision: supplier.subDivision ?? '',
    localStreet: supplier.localStreet ?? '',
    localStreet2: supplier.localStreet2 ?? '',
    localStreetNumber: supplier.localStreetNumber ?? '',
    localSubDivision: supplier.localSubDivision ?? '',
    localPostCode: supplier.localPostCode ?? '',
    localPoBox: supplier.localPoBox ?? '',
    localCity: supplier.localCity ?? '',
    contactName: supplier.contactName ?? '',
    contactEmail: supplier.contactEmail ?? '',
    contactPhoneNumber: supplier.contactPhoneNumber ?? '',
  }
}

function emptyToNull(value: string): string | null {
  return value.trim() === '' ? null : value
}

/** When switching to a local-address country, copy Latin fields into empty local fields. */
export function applySupplierOrganizationCountryChange(
  prev: SupplierOrganizationFormState,
  countryCode: string
): SupplierOrganizationFormState {
  const next = { ...prev, country: countryCode }

  if (countryCode.trim().toLowerCase() !== 'cn') {
    return next
  }

  return {
    ...next,
    localStreet: next.localStreet.trim() ? next.localStreet : next.street,
    localStreet2: next.localStreet2.trim() ? next.localStreet2 : next.street2,
    localStreetNumber: next.localStreetNumber.trim()
      ? next.localStreetNumber
      : next.streetNumber,
    localSubDivision: next.localSubDivision.trim()
      ? next.localSubDivision
      : next.subDivision,
    localPoBox: next.localPoBox.trim() ? next.localPoBox : next.poBox,
    localCity: next.localCity.trim() ? next.localCity : next.city,
    localPostCode: next.localPostCode.trim() ? next.localPostCode : next.postCode,
  }
}

export function buildUpdatePayload(
  state: SupplierOrganizationFormState
): SupplierOrganizationUpdatePayload {
  return {
    name: state.name.trim() || undefined,
    localName: emptyToNull(state.localName),
    businessRegistrationNumber: emptyToNull(state.businessRegistrationNumber),
    street: emptyToNull(state.street),
    street2: emptyToNull(state.street2),
    streetNumber: emptyToNull(state.streetNumber),
    subDivision: emptyToNull(state.subDivision),
    country: (() => {
      const code = state.country.trim()
      return code.length === 2 ? code.toLowerCase() : emptyToNull(state.country)
    })(),
    postCode: emptyToNull(state.postCode),
    poBox: emptyToNull(state.poBox),
    city: emptyToNull(state.city),
    localStreet: emptyToNull(state.localStreet),
    localStreet2: emptyToNull(state.localStreet2),
    localStreetNumber: emptyToNull(state.localStreetNumber),
    localSubDivision: emptyToNull(state.localSubDivision),
    localPostCode: emptyToNull(state.localPostCode),
    localPoBox: emptyToNull(state.localPoBox),
    localCity: emptyToNull(state.localCity),
    contactName: emptyToNull(state.contactName),
    contactPhoneNumber: emptyToNull(state.contactPhoneNumber),
    contactEmail: emptyToNull(state.contactEmail),
  }
}